import React, { useState, useMemo, useEffect } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { getWeatherEmoji } from "./WeatherService";

// ─── Weather condition emoji mapper for forecast ──────────────────────────
const getForecastEmoji = (conditionType, isDaytime = true) => {
  const map = {
    CLEAR: isDaytime ? "☀️" : "🌙",
    SUNNY: "☀️",
    MOSTLY_SUNNY: "🌤️",
    PARTLY_SUNNY: "⛅",
    MOSTLY_CLEAR: isDaytime ? "🌤️" : "🌙",
    PARTLY_CLOUDY: isDaytime ? "⛅" : "☁️",
    MOSTLY_CLOUDY: "🌥️",
    CLOUDY: "☁️",
    OVERCAST: "☁️",
    FOG: "🌫️",
    LIGHT_FOG: "🌫️",
    DRIZZLE: "🌦️",
    LIGHT_RAIN: "🌦️",
    RAIN: "🌧️",
    HEAVY_RAIN: "🌧️",
    RAIN_SHOWERS: "🌦️",
    SNOW: "🌨️",
    LIGHT_SNOW: "🌨️",
    HEAVY_SNOW: "❄️",
    SNOW_SHOWERS: "🌨️",
    FLURRIES: "🌨️",
    FREEZING_RAIN: "🌧️",
    FREEZING_DRIZZLE: "🌧️",
    ICE_PELLETS: "🧊",
    SLEET: "🌨️",
    THUNDERSTORM: "⛈️",
    THUNDERSTORMS: "⛈️",
    HAIL: "🧊",
    TORNADO: "🌪️",
    TROPICAL_STORM: "🌀",
    HURRICANE: "🌀",
    WINDY: "💨",
    BLOWING_SNOW: "🌬️",
    BLIZZARD: "❄️",
    HAZE: "🌫️",
    SMOKE: "🌫️",
    DUST: "🌫️",
    SANDSTORM: "🌫️",
  };
  return map[conditionType] || (isDaytime ? "🌤️" : "🌙");
};

const getMoonEmoji = (phase) => {
  const map = {
    NEW_MOON: "🌑",
    WAXING_CRESCENT: "🌒",
    FIRST_QUARTER: "🌓",
    WAXING_GIBBOUS: "🌔",
    FULL_MOON: "🌕",
    WANING_GIBBOUS: "🌖",
    LAST_QUARTER: "🌗",
    THIRD_QUARTER: "🌗",
    WANING_CRESCENT: "🌘",
  };
  return map[phase] || "🌙";
};

const formatMoonPhase = (phase) => {
  if (!phase) return "";
  return phase
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getSeverity = (temp, windSpeed, precipProb, uvIndex) => {
  let score = 0;
  if (temp != null) {
    if (temp <= -20 || temp >= 45) score += 3;
    else if (temp <= -10 || temp >= 40) score += 2;
    else if (temp <= 0 || temp >= 35) score += 1;
  }
  if (windSpeed != null) {
    if (windSpeed >= 60) score += 3;
    else if (windSpeed >= 40) score += 2;
    else if (windSpeed >= 25) score += 1;
  }
  if (precipProb != null) {
    if (precipProb >= 70) score += 2;
    else if (precipProb >= 40) score += 1;
  }
  if (uvIndex != null && uvIndex >= 8) score += 1;

  if (score >= 5) return { label: "HIGH", color: "#ef4444", bg: "#fef2f2" };
  if (score >= 3) return { label: "MEDIUM", color: "#f59e0b", bg: "#fffbeb" };
  if (score >= 1) return { label: "LOW", color: "#3b82f6", bg: "#eff6ff" };
  return { label: "NORMAL", color: "#10b981", bg: "#ecfdf5" };
};

const getTempColor = (temp) => {
  if (temp == null) return "#666";
  if (temp <= -10) return "#6366f1";
  if (temp <= 0) return "#3b82f6";
  if (temp <= 10) return "#06b6d4";
  if (temp <= 20) return "#10b981";
  if (temp <= 30) return "#f59e0b";
  if (temp <= 40) return "#ef4444";
  return "#dc2626";
};

const formatHour = (displayDateTime) => {
  if (!displayDateTime) return "--";
  const h = displayDateTime.hours ?? 0;
  if (h === 0) return "12:00 am";
  if (h === 12) return "12:00 pm";
  if (h < 12) return `${String(h).padStart(2, "0")}:00 am`;
  return `${String(h - 12).padStart(2, "0")}:00 pm`;
};

const formatDayLabel = (displayDate, index) => {
  if (!displayDate) return "--";
  const { year, month, day } = displayDate;
  const d = new Date(year, month - 1, day);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (index === 0) return "Today";
  return `${days[d.getDay()]}, ${day} ${months[month - 1]}`;
};

const formatDateSmall = (displayDateTime) => {
  if (!displayDateTime) return "";
  const { year, month, day } = displayDateTime;
  const d = new Date(year, month - 1, day);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${days[d.getDay()]}, ${day} ${months[month - 1]}`;
};

const formatTime12h = (isoString) => {
  if (!isoString) return "--";
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch {
    return "--";
  }
};

// ═══════════════════════════════════════════════════════════════════════════
const SeverityBadge = ({ severity }) => (
  <span
    style={{
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: "20px",
      fontSize: "9px",
      fontWeight: "700",
      letterSpacing: "0.6px",
      color: severity.color,
      backgroundColor: severity.bg,
      border: `1px solid ${severity.color}25`,
      whiteSpace: "nowrap",
    }}
  >
    {severity.label}
  </span>
);

// ═══════════════════════════════════════════════════════════════════════════
const HourlyCard = ({ hour, index, isDark, isPastData }) => {
  const conditionType = hour.weatherCondition?.type || "CLEAR";
  const conditionText = hour.weatherCondition?.description?.text || "Clear";
  const temp = hour.temperature?.degrees;
  const feelsLike = hour.feelsLikeTemperature?.degrees;
  const humidity = hour.humidity?.percent;
  const windSpeed = hour.wind?.speed?.value;
  const windDir = hour.wind?.direction?.cardinal || "";
  const uvIndex = hour.uvIndex;
  const precipProb = hour.precipitation?.probability?.percent;
  const isDaytime = hour.isDaytime !== false;
  const emoji = getForecastEmoji(conditionType, isDaytime);
  const severity = getSeverity(temp, windSpeed, precipProb, uvIndex);

  const timeLabel = (!isPastData && index === 0) ? "Now" : formatHour(hour.displayDateTime);
  const dateLabel = formatDateSmall(hour.displayDateTime);

  return (
    <div
      style={{
        background: isDark
          ? "linear-gradient(135deg, #1a2540, #1e2d4a)"
          : "#ffffff",
        borderRadius: "10px",
        padding: "12px 14px",
        marginBottom: "8px",
        border: isDark ? "1px solid rgba(42,63,95,0.4)" : "1px solid rgba(30,58,95,0.08)",
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow 0.2s ease",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: `linear-gradient(90deg, ${getTempColor(temp)}44, ${getTempColor(temp)})`,
          borderRadius: "2px",
        }}
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flex: 1, minWidth: 0 }}>
          <div>
            <div
              style={{
                fontSize: "13px",
                fontWeight: "700",
                color: (!isPastData && index === 0) ? "#2a7de1" : isDark ? "#e0e8f0" : "#1a2a3a",
              }}
            >
              {timeLabel}
            </div>
            <div style={{ fontSize: "10px", color: isDark ? "#556677" : "#8899aa", marginTop: "1px" }}>
              {dateLabel}
            </div>
          </div>
          <span style={{ fontSize: "30px", lineHeight: 1 }}>{emoji}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: "4px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "20px", fontWeight: "700", color: getTempColor(temp) }}>
                {temp != null ? `${Math.round(temp)}°C` : "--"}
              </span>
              <span style={{ fontSize: "11px", color: isDark ? "#667788" : "#8899aa" }}>
                Feels {feelsLike != null ? `${Math.round(feelsLike)}°` : "--"}
              </span>
            </div>
            <div style={{ fontSize: "11px", color: isDark ? "#8899aa" : "#5a6a7a", marginTop: "2px" }}>
              {conditionText}
            </div>
          </div>
        </div>
        <SeverityBadge severity={severity} />
      </div>

      <div
        style={{
          display: "flex",
          gap: "12px",
          marginTop: "8px",
          paddingTop: "8px",
          borderTop: isDark ? "1px solid rgba(42,63,95,0.3)" : "1px solid rgba(30,58,95,0.06)",
          flexWrap: "wrap",
        }}
      >
        {humidity != null && <span style={{ fontSize: "11px", color: isDark ? "#6688aa" : "#6688aa" }}>💧 {humidity}%</span>}
        {windSpeed != null && <span style={{ fontSize: "11px", color: isDark ? "#6688aa" : "#6688aa" }}>💨 {Math.round(windSpeed)} km/h {windDir}</span>}
        {uvIndex != null && <span style={{ fontSize: "11px", color: isDark ? "#6688aa" : "#6688aa" }}>☀️ UV{uvIndex}</span>}
        {precipProb != null && precipProb > 0 && <span style={{ fontSize: "11px", color: isDark ? "#6688aa" : "#6688aa" }}>🌧️ {precipProb}%</span>}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
const DailyCard = ({ day, index, isDark }) => {
  const dayForecast = day.daytimeForecast || {};
  const nightForecast = day.nighttimeForecast || {};
  const maxTemp = day.maxTemperature?.degrees;
  const minTemp = day.minTemperature?.degrees;
  const dayCondType = dayForecast.weatherCondition?.type || "CLEAR";
  const nightCondType = nightForecast.weatherCondition?.type || "CLEAR";
  const dayCondText = dayForecast.weatherCondition?.description?.text || "Clear";
  const nightCondText = nightForecast.weatherCondition?.description?.text || "Clear";
  const dayEmoji = getForecastEmoji(dayCondType, true);
  const nightEmoji = getForecastEmoji(nightCondType, false);
  const humidity = dayForecast.humidity?.percent ?? day.humidity?.percent;
  const windSpeed = dayForecast.wind?.speed?.value;
  const windDir = dayForecast.wind?.direction?.cardinal || "";
  const rainProb = dayForecast.precipitation?.probability?.percent;
  const thunderProb = dayForecast.thunderProbability?.percent;
  const sunrise = day.sunEvents?.sunriseTime;
  const sunset = day.sunEvents?.sunsetTime;
  const moonPhase = day.moonPhase;
  const severity = getSeverity(maxTemp, windSpeed, rainProb, null);
  const dayLabel = formatDayLabel(day.displayDate, index);

  return (
    <div
      style={{
        background: isDark
          ? "linear-gradient(135deg, #1a2540, #1e2d4a)"
          : "#ffffff",
        borderRadius: "10px",
        padding: "12px 14px",
        marginBottom: "10px",
        border: isDark ? "1px solid rgba(42,63,95,0.4)" : "1px solid rgba(30,58,95,0.08)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: `linear-gradient(90deg, ${getTempColor(minTemp)}44, ${getTempColor(maxTemp)})`,
        }}
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "13px", fontWeight: "700", color: index === 0 ? "#2a7de1" : isDark ? "#e0e8f0" : "#1a2a3a" }}>
            {dayLabel}
          </div>
          <div style={{ fontSize: "11px", color: isDark ? "#8899aa" : "#5a6a7a", marginTop: "1px" }}>
            {dayCondText}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "8px", color: isDark ? "#667788" : "#999", fontWeight: "700", marginBottom: "2px", letterSpacing: "0.5px" }}>DAY</div>
            <span style={{ fontSize: "26px", lineHeight: 1 }}>{dayEmoji}</span>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "8px", color: isDark ? "#667788" : "#999", fontWeight: "700", marginBottom: "2px", letterSpacing: "0.5px" }}>NIGHT</div>
            <span style={{ fontSize: "26px", lineHeight: 1 }}>{nightEmoji}</span>
          </div>
          <div style={{ textAlign: "right", minWidth: "46px" }}>
            <div style={{ fontSize: "17px", fontWeight: "700", color: getTempColor(maxTemp), lineHeight: 1.2 }}>
              {maxTemp != null ? `${Math.round(maxTemp)}°` : "--"}
            </div>
            <div style={{ fontSize: "13px", fontWeight: "600", color: isDark ? "#556677" : "#8899aa", lineHeight: 1.2 }}>
              {minTemp != null ? `${Math.round(minTemp)}°` : "--"}
            </div>
            <div style={{ fontSize: "8px", color: isDark ? "#445566" : "#aaa", letterSpacing: "0.3px" }}>H / L</div>
          </div>
          <SeverityBadge severity={severity} />
        </div>
      </div>

      <div style={{ fontSize: "11px", color: isDark ? "#6688aa" : "#7799aa", marginTop: "4px" }}>
        🌙 Night: {nightCondText}
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          marginTop: "8px",
          paddingTop: "8px",
          borderTop: isDark ? "1px solid rgba(42,63,95,0.3)" : "1px solid rgba(30,58,95,0.06)",
          flexWrap: "wrap",
          fontSize: "11px",
          color: isDark ? "#6688aa" : "#6688aa",
        }}
      >
        {rainProb != null && <span>🌧️ Rain {rainProb}%</span>}
        {thunderProb != null && thunderProb > 0 && <span>⚡ Thunder {thunderProb}%</span>}
        {windSpeed != null && <span>💨 {Math.round(windSpeed)} km/h</span>}
        {humidity != null && <span>💧 {humidity}%</span>}
        {sunrise && <span>🌅 {formatTime12h(sunrise)}</span>}
        {sunset && <span>🌇 {formatTime12h(sunset)}</span>}
      </div>

      {moonPhase && (
        <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "6px", fontSize: "11px", color: isDark ? "#556677" : "#8899aa" }}>
          {getMoonEmoji(moonPhase)} {formatMoonPhase(moonPhase)}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN WEATHER PANEL
// ═══════════════════════════════════════════════════════════════════════════
const WeatherPanel = ({
  weatherData,
  isLoading,
  theme,
  dropdownOptions,
  selectedWeatherRegion,
  onRegionSelect,
  weatherFilterDate,
  onDateChange,
  dropdownLoading,
  forecastHourly,
  forecastDaily,
  forecastLoading,
  isPastDate,
  pastHourlyData,
}) => {
  const [expandedCards, setExpandedCards] = useState({});
  const [activeSubTab, setActiveSubTab] = useState("current");
  const isDark = theme === "dark";

  // ─── Theme tokens matching parent panel ──────────────────────
  const tokens = {
    cardBg: isDark ? "#1a2540" : "#ffffff",
    cardBorder: isDark ? "1px solid rgba(74,95,122,0.35)" : "1px solid rgba(46,109,164,0.1)",
    sectionHeaderBg: isDark
      ? "linear-gradient(135deg, #162040, #1a2a50)"
      : "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)",
    sectionBodyBg: isDark ? "#1a2540" : "#f8fafd",
    textPrimary: isDark ? "#e2e8f0" : "#1a2a3a",
    textSecondary: isDark ? "#8899aa" : "#5a6a7a",
    textMuted: isDark ? "#556677" : "#8899aa",
    inputBg: isDark ? "#0f1a2e" : "#ffffff",
    inputBorder: isDark ? "#2a3f5f" : "#d0dce8",
    accentColor: isDark ? "#2a7de1" : "#2E6DA4",
    accentLight: isDark ? "rgba(42,125,225,0.15)" : "rgba(46,109,164,0.08)",
    shadow: isDark ? "0 4px 24px rgba(0,0,0,0.4)" : "0 4px 24px rgba(46,109,164,0.08)",
    divider: isDark ? "rgba(74,95,122,0.25)" : "rgba(46,109,164,0.08)",
  };

  const toggleCard = (index) => {
    setExpandedCards((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  useEffect(() => {
    if (isPastDate) {
      setActiveSubTab("hourly");
    } else {
      setActiveSubTab("current");
    }
  }, [isPastDate]);

  const groupedOptions = useMemo(() => {
    if (!dropdownOptions || dropdownOptions.length === 0) return [];
    const districtMap = new Map();
    dropdownOptions.forEach((opt) => {
      if (opt.type === "district") {
        if (!districtMap.has(opt.districtName)) {
          districtMap.set(opt.districtName, { district: opt, blocks: [] });
        }
      }
    });
    dropdownOptions.forEach((opt) => {
      if (opt.type === "block" && districtMap.has(opt.districtName)) {
        districtMap.get(opt.districtName).blocks.push(opt);
      }
    });
    return Array.from(districtMap.values());
  }, [dropdownOptions]);

  const allSubTabs = [
    { key: "current", label: "Current", icon: "🌡️" },
    { key: "hourly", label: "24-Hour", icon: "⏰" },
    { key: "daily", label: "5-Day", icon: "🗓️" },
  ];

  const subTabs = isPastDate
    ? allSubTabs.filter((tab) => tab.key === "hourly")
    : allSubTabs;

  const hasRegionSelected = !!selectedWeatherRegion;

  const activeHourlyData = isPastDate ? pastHourlyData : forecastHourly;

  const hasWeatherData =
    (weatherData && weatherData.length > 0) ||
    (forecastHourly && forecastHourly.length > 0) ||
    (forecastDaily && forecastDaily.length > 0) ||
    (pastHourlyData && pastHourlyData.length > 0);

  return (
    <div
      style={{
        borderRadius: "12px",
        marginBottom: "12px",
        overflow: "hidden",
        border: tokens.cardBorder,
        boxShadow: `0 1px 4px ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(30,58,95,0.06)'}`,
        transition: "box-shadow 0.2s ease",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: tokens.sectionHeaderBg,
          color: "white",
          padding: "10px 14px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "600",
          fontSize: "12.5px",
          letterSpacing: "0.03em",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          🌤️ Weather Report
        </span>
        {hasRegionSelected && hasWeatherData && (
          <span style={{
            fontSize: "10px",
            fontWeight: "500",
            opacity: 0.9,
            padding: "2px 8px",
            background: "rgba(255,255,255,0.15)",
            borderRadius: "10px",
          }}>
            {isPastDate ? "📅 Historical" : "🔴 Live"}
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: "12px 14px", background: tokens.sectionBodyBg }}>

        {/* Region dropdown */}
        <div style={{ marginBottom: "10px" }}>
          <label style={{
            display: "block",
            fontSize: "11px",
            fontWeight: "600",
            color: tokens.textSecondary,
            marginBottom: "5px",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}>
            Select Region
          </label>
          {dropdownLoading ? (
            <div style={{ padding: "8px", textAlign: "center", fontSize: "12px", color: tokens.textMuted }}>
              Loading regions...
            </div>
          ) : (
            <select
              value={selectedWeatherRegion || ""}
              onChange={(e) => onRegionSelect && onRegionSelect(e.target.value)}
              className="gis-select"
            >
              <option value="">-- Select District / Block --</option>
              {groupedOptions.map((group) => (
                <optgroup key={group.district.value} label={group.district.districtName}>
                  <option value={group.district.value}>
                    {group.district.districtName} (District)
                  </option>
                  {group.blocks.map((block) => (
                    <option key={block.value} value={block.value}>
                      {block.label} ({block.districtName})
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          )}
        </div>

        {/* Date picker */}
        {hasRegionSelected && (
          <div style={{ marginBottom: "12px" }}>
            <label style={{
              display: "block",
              fontSize: "11px",
              fontWeight: "600",
              color: tokens.textSecondary,
              marginBottom: "6px",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}>
              Date
            </label>

            {/* Quick-access buttons */}
            <div style={{ display: "flex", gap: "3px", marginBottom: "8px" }}>
              {(() => {
                const today = new Date();
                const todayStr = today.toISOString().split("T")[0];
                const buttons = [];
                const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

                for (let i = 5; i >= 1; i--) {
                  const d = new Date(today);
                  d.setDate(d.getDate() - i);
                  const dateStr = d.toISOString().split("T")[0];
                  const dayNum = d.getDate();
                  const monthLabel = months[d.getMonth()];
                  const isActive = weatherFilterDate === dateStr;

                  buttons.push(
                    <button
                      key={`past-${i}`}
                      onClick={() => onDateChange && onDateChange(dateStr)}
                      title={dateStr}
                      style={{
                        flex: 1,
                        padding: "5px 2px",
                        border: isActive
                          ? `2px solid ${tokens.accentColor}`
                          : `1px solid ${tokens.inputBorder}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "10px",
                        fontWeight: isActive ? "700" : "500",
                        backgroundColor: isActive
                          ? tokens.accentLight
                          : tokens.cardBg,
                        color: isActive
                          ? tokens.accentColor
                          : tokens.textSecondary,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        lineHeight: "1.2",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <span style={{ fontSize: "13px", fontWeight: "700" }}>{dayNum}</span>
                      <span style={{ fontSize: "8px", opacity: 0.7 }}>{monthLabel}</span>
                    </button>
                  );
                }

                const isTodayActive = weatherFilterDate === todayStr;
                buttons.push(
                  <button
                    key="today"
                    onClick={() => onDateChange && onDateChange(todayStr)}
                    title={todayStr}
                    style={{
                      flex: 1,
                      padding: "5px 2px",
                      border: isTodayActive
                        ? `2px solid ${tokens.accentColor}`
                        : `1px solid ${tokens.inputBorder}`,
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "10px",
                      fontWeight: isTodayActive ? "700" : "500",
                      backgroundColor: isTodayActive
                        ? tokens.accentColor
                        : tokens.cardBg,
                      color: isTodayActive
                        ? "#ffffff"
                        : tokens.textSecondary,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      lineHeight: "1.2",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <span style={{ fontSize: "11px", fontWeight: "700" }}>Today</span>
                    <span style={{ fontSize: "8px", opacity: 0.8 }}>
                      {today.getDate()} {months[today.getMonth()]}
                    </span>
                  </button>
                );

                return buttons;
              })()}
            </div>

            <input
              type="date"
              value={weatherFilterDate || ""}
              onChange={(e) => onDateChange && onDateChange(e.target.value)}
              max={new Date().toISOString().split("T")[0]}
              style={{
                paddingBottom: "20px",
                border: "1px solid rgb(221, 221, 221)",
                borderRadius: "4px",
                fontSize: "12px",
                height: "35px",
                paddingLeft: "5px",
                width: "100%",
              }}
            />
          </div>
        )}

        {/* Sub-Tabs */}
        {hasRegionSelected && hasWeatherData && (
          <div
            style={{
              display: "flex",
              gap: "3px",
              marginBottom: "12px",
              padding: "3px",
              backgroundColor: isDark ? "#0f1a2e" : "rgba(30,58,95,0.06)",
              borderRadius: "10px",
            }}
          >
            {subTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveSubTab(tab.key)}
                style={{
                  flex: 1,
                  padding: "7px 6px",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: activeSubTab === tab.key ? "700" : "500",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  transition: "all 0.2s ease",
                  backgroundColor: activeSubTab === tab.key
                    ? (isDark ? tokens.accentColor : "#ffffff")
                    : "transparent",
                  color: activeSubTab === tab.key
                    ? (isDark ? "#ffffff" : tokens.accentColor)
                    : tokens.textMuted,
                  boxShadow: activeSubTab === tab.key
                    ? (isDark ? "none" : "0 2px 8px rgba(42,125,225,0.15)")
                    : "none",
                }}
              >
                <span style={{ fontSize: "12px" }}>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Loading */}
        {(isLoading || forecastLoading) && (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            gap: "8px",
            color: tokens.textSecondary,
            fontSize: "12px",
          }}>
            <span style={{
              width: 16, height: 16,
              border: `2px solid ${tokens.accentColor}`,
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "weather-spin 0.8s linear infinite",
              display: "inline-block",
            }} />
            Fetching weather data...
            <style>{`@keyframes weather-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* No selection */}
        {!isLoading && !forecastLoading && !hasRegionSelected && (
          <div style={{ padding: "16px", textAlign: "center", color: tokens.textMuted, fontSize: "12px" }}>
            Select a district or block to see weather data
          </div>
        )}

        {!isLoading && !forecastLoading && hasRegionSelected && !hasWeatherData && (
          <div style={{ padding: "16px", textAlign: "center", color: tokens.textMuted, fontSize: "12px" }}>
            Select a date to fetch weather data
          </div>
        )}

        {/* ═══ CURRENT TAB ═══ */}
        {activeSubTab === "current" && !isPastDate && !isLoading &&
          weatherData?.map((weather, index) => {
            const emoji = getWeatherEmoji(weather.condition);
            const isCardExpanded = expandedCards[index] !== false;

            return (
              <div
                key={`${weather.gisId}-${index}`}
                style={{
                  backgroundColor: tokens.cardBg,
                  borderRadius: "10px",
                  marginBottom: "8px",
                  overflow: "hidden",
                  border: tokens.cardBorder,
                  transition: "box-shadow 0.2s ease",
                }}
              >
                <div
                  onClick={() => toggleCard(index)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    cursor: "pointer",
                    background: isDark
                      ? "linear-gradient(135deg, #1a2540 0%, #1e2d4a 100%)"
                      : "linear-gradient(135deg, #f8fafd 0%, #eef4f9 100%)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "22px" }}>{emoji}</span>
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: "600", color: tokens.textPrimary, lineHeight: "1.2" }}>
                        {weather.regionName}
                      </div>
                      <div style={{ fontSize: "11px", color: tokens.textSecondary, marginTop: "1px" }}>
                        {weather.condition}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "18px", fontWeight: "700", color: getTempColor(weather.temperature) }}>
                      {weather.temperature != null ? `${Math.round(weather.temperature)}${weather.temperatureUnit}` : "--"}
                    </span>
                    {isCardExpanded ? (
                      <FaChevronUp size={10} color={tokens.textMuted} />
                    ) : (
                      <FaChevronDown size={10} color={tokens.textMuted} />
                    )}
                  </div>
                </div>

                {isCardExpanded && (
                  <div style={{ padding: "10px 12px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", marginBottom: "6px" }}>
                      <MetricBox icon="💧" label="Humidity" value={weather.humidity != null ? `${weather.humidity}%` : "--"} isDark={isDark} tokens={tokens} />
                      <MetricBox icon="💨" label="Wind" value={weather.windSpeed != null ? `${Math.round(weather.windSpeed)} km/h ${weather.windDirectionCardinal || ""}` : "--"} isDark={isDark} tokens={tokens} />
                      <MetricBox icon="🌡️" label="Feels Like" value={weather.feelsLike != null ? `${Math.round(weather.feelsLike)}${weather.temperatureUnit}` : "--"} isDark={isDark} tokens={tokens} />
                      <MetricBox icon="☁️" label="Cloud Cover" value={weather.cloudCover != null ? `${weather.cloudCover}%` : "--"} isDark={isDark} tokens={tokens} />
                      {weather.pressure != null && <MetricBox icon="📊" label="Pressure" value={`${Math.round(weather.pressure)} hPa`} isDark={isDark} tokens={tokens} />}
                      {weather.precipitation != null && <MetricBox icon="🌧️" label="Precipitation" value={`${weather.precipitation} mm`} isDark={isDark} tokens={tokens} />}
                      {weather.uvIndex != null && <MetricBox icon="☀️" label="UV Index" value={weather.uvIndex} isDark={isDark} tokens={tokens} />}
                      {weather.visibility != null && <MetricBox icon="👁️" label="Visibility" value={`${weather.visibility} km`} isDark={isDark} tokens={tokens} />}
                    </div>
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: "10px",
                      color: tokens.textMuted,
                      paddingTop: "6px",
                      borderTop: `1px solid ${tokens.divider}`,
                    }}>
                      <span>📍 {weather.latitude?.toFixed(4)}, {weather.longitude?.toFixed(4)}</span>
                      <span>
                        Updated: {weather.fetchedAt ? new Date(weather.fetchedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

        {/* ═══ 24-HOUR TAB ═══ */}
        {activeSubTab === "hourly" && !isLoading && !forecastLoading && (
          <div>
            {activeHourlyData && activeHourlyData.length > 0 ? (
              <>
                <div style={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color: tokens.textMuted,
                  marginBottom: "10px",
                  letterSpacing: "0.6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  textTransform: "uppercase",
                }}>
                  {isPastDate
                    ? `📅 Historical — ${weatherFilterDate}`
                    : "⏰ Next 24 Hours"}
                </div>
                <div style={{ maxHeight: "500px", overflowY: "auto", paddingRight: "4px" }}>
                  {activeHourlyData.map((hour, idx) => (
                    <HourlyCard key={idx} hour={hour} index={idx} isDark={isDark} isPastData={isPastDate} />
                  ))}
                </div>
              </>
            ) : (
              <div style={{ padding: "20px", textAlign: "center", color: tokens.textMuted, fontSize: "12px" }}>
                {hasRegionSelected
                  ? isPastDate
                    ? "No historical weather data available for this date"
                    : "No hourly forecast data available"
                  : "Select a region to see hourly forecast"}
              </div>
            )}
          </div>
        )}

        {/* ═══ 5-DAY TAB ═══ */}
        {activeSubTab === "daily" && !isPastDate && !forecastLoading && (
          <div>
            {forecastDaily && forecastDaily.length > 0 ? (
              <>
                <div style={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color: tokens.textMuted,
                  marginBottom: "10px",
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                }}>
                  🗓️ 5-Day Forecast
                </div>
                <div style={{ maxHeight: "500px", overflowY: "auto", paddingRight: "4px" }}>
                  {forecastDaily.map((day, idx) => (
                    <DailyCard key={idx} day={day} index={idx} isDark={isDark} />
                  ))}
                </div>
              </>
            ) : (
              <div style={{ padding: "20px", textAlign: "center", color: tokens.textMuted, fontSize: "12px" }}>
                {hasRegionSelected ? "No daily forecast data available" : "Select a region to see 5-day forecast"}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Metric Box ─────────────────────────────────────────────────────────────
const MetricBox = ({ icon, label, value, isDark, tokens }) => (
  <div
    style={{
      backgroundColor: isDark ? "#0f1a2e" : "#f4f7fb",
      borderRadius: "8px",
      padding: "7px 10px",
      border: isDark ? "1px solid rgba(42,63,95,0.3)" : "1px solid rgba(30,58,95,0.06)",
    }}
  >
    <div style={{ fontSize: "10px", color: isDark ? "#556677" : "#8899aa", marginBottom: "3px" }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: "13px", fontWeight: "600", color: isDark ? "#d0d8e0" : "#1a2a3a" }}>
      {value}
    </div>
  </div>
);

export default WeatherPanel;